import {
  MathNode,
  isAccessorNode,
  isConstantNode,
  isFunctionNode,
  isSymbolNode,
  type IndexNode,
} from 'mathjs';
import { uniq } from 'lodash';
import { getKeys } from '@shared/helpers/object.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { emptyStoreySummaryVariables } from '@shared/models/design/design-expression-variables.model';
import { mathJS } from './mathjs-instance';

export type ExpressionPropertyAccessRoot =
  'getStorey' | 'parent' | 'getParentValue';

export type ExpressionPropertyAccess = {
  root: ExpressionPropertyAccessRoot;
  /** Property path after the root (dot-separated segments, e.g. `programs.stairwells.gfa`). */
  path: string[];
};

const GET_STOREY_FUNCTION_NAMES = new Set(['getStorey']);

const ALLOWED_GET_STOREY_PROPERTY_KEYS = new Set<string>(
  getKeys(emptyStoreySummaryVariables),
);

/**
 * Read a single accessor segment from dot or bracket notation.
 * Symbol indexes (e.g. `parent[area_side]`) resolve to the symbol name.
 */
const getIndexSegment = (index: IndexNode): string | undefined => {
  const dimension = index.dimensions[0];
  if (!dimension) return undefined;

  if (isConstantNode(dimension)) {
    const { value } = dimension;
    if (guards.is.string(value)) return value;
    if (guards.is.number(value)) return String(value);
    return undefined;
  }

  if (isSymbolNode(dimension)) {
    return dimension.name;
  }

  return undefined;
};

/**
 * Unwrap a member-access chain into its base node and path segments (outermost segment last).
 */
const unwrapAccessorChain = (
  node: MathNode,
): { base: MathNode; path: string[] } | undefined => {
  if (!isAccessorNode(node)) return undefined;

  const path: string[] = [];
  let current: MathNode = node;

  while (isAccessorNode(current)) {
    const segment = getIndexSegment(current.index);
    if (!segment) return undefined;
    path.unshift(segment);
    current = current.object;
  }

  return { base: current, path };
};

/**
 * True when `node` is `parent` or a chain of `parent.parent...`.
 */
const isParentScopeNode = (node: MathNode): boolean => {
  if (isSymbolNode(node)) return node.name === 'parent';
  if (!isAccessorNode(node)) return false;
  const segment = getIndexSegment(node.index);
  if (segment !== 'parent') return false;
  return isParentScopeNode(node.object);
};

/**
 * Method name of a call on inherited parent scope (`parent.getProduct(...)`).
 */
const getParentMemberCallName = (fn: MathNode): string | undefined => {
  if (!isAccessorNode(fn)) return undefined;
  const methodName = getIndexSegment(fn.index);
  if (!methodName) return undefined;
  if (!isParentScopeNode(fn.object)) return undefined;
  return methodName;
};

/**
 * Name of a called function, including `parent.getStorey(...)` (accessor + call).
 */
const getCalledFunctionName = (fn: MathNode): string | undefined => {
  if (isSymbolNode(fn)) return fn.name;
  const methodName = getParentMemberCallName(fn);
  if (methodName && GET_STOREY_FUNCTION_NAMES.has(methodName)) {
    return methodName;
  }
  return undefined;
};

/**
 * Resolve which expression object root a member chain starts from.
 */
const resolvePropertyAccessRoot = (
  base: MathNode,
): ExpressionPropertyAccessRoot | undefined => {
  if (isFunctionNode(base)) {
    const fnName = getCalledFunctionName(base.fn);
    if (fnName && GET_STOREY_FUNCTION_NAMES.has(fnName)) return 'getStorey';
    if (fnName === 'getParentValue') return 'getParentValue';
  }

  if (isSymbolNode(base) && base.name === 'parent') return 'parent';

  return undefined;
};

/**
 * String literal from a math.js constant argument.
 */
const getStringLiteralArg = (
  node: MathNode | undefined,
): string | undefined => {
  if (!node || !isConstantNode(node)) return undefined;
  const { value } = node;
  if (!guards.is.string(value)) return undefined;
  return value;
};

/**
 * Called function name, including `parent.getStorey` / `parent.getProperty`.
 */
const getFunctionSymbolName = (fn: MathNode): string | undefined => {
  if (isSymbolNode(fn)) return fn.name;
  return getParentMemberCallName(fn);
};

/**
 * {@link getParentValue} uses a string literal property name as its first argument.
 */
const getGetParentValuePath = (node: MathNode): string[] | undefined => {
  if (!isFunctionNode(node)) return undefined;
  if (getFunctionSymbolName(node.fn) !== 'getParentValue') return undefined;
  const value = getStringLiteralArg(node.args[0]);
  return value ? [value] : undefined;
};

/**
 * {@link getProperty} uses `(object, "name")`. Map to the same roots as member access
 * so `getProperty(parent, "heating_hours")` is caught by the obsolete-variable audit.
 */
const getGetPropertyAccess = (
  node: MathNode,
): ExpressionPropertyAccess | undefined => {
  if (!isFunctionNode(node)) return undefined;
  if (getFunctionSymbolName(node.fn) !== 'getProperty') return undefined;

  const propertyName = getStringLiteralArg(node.args[1]);
  if (!propertyName) return undefined;

  const target = node.args[0];
  if (!target) return undefined;

  if (isParentScopeNode(target)) {
    return { root: 'parent', path: [propertyName] };
  }

  if (isFunctionNode(target)) {
    const called = getCalledFunctionName(target.fn);
    if (called && GET_STOREY_FUNCTION_NAMES.has(called)) {
      return { root: 'getStorey', path: [propertyName] };
    }
  }

  // Symbol / other object: still record the literal so removed names fail the audit
  return { root: 'getParentValue', path: [propertyName] };
};

const dedupePropertyAccesses = (
  accesses: ExpressionPropertyAccess[],
): ExpressionPropertyAccess[] => {
  const seen = new Set<string>();
  return accesses.filter(({ root, path }) => {
    const key = `${root}:${path.join('.')}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Collect property paths read from expression object roots:
 * - {@link getStorey} return value (`getStorey(1).inner_height`, `parent.getStorey().gfa`)
 * - `parent` (`parent.area`, `parent["area_side"]`, `parent[dynamic_key]`)
 * - {@link getParentValue} (same property names as on `parent`)
 * - {@link getProperty} string-literal second argument (`getProperty(parent, "gfa")`)
 *
 * @param expression - Math.js expression string
 */
export const getExpressionPropertyAccesses = (
  expression: string | undefined,
): ExpressionPropertyAccess[] => {
  if (!expression) return [];

  const accesses: ExpressionPropertyAccess[] = [];

  try {
    const ast = mathJS.parse(expression);
    const accessorNodes: MathNode[] = [];
    const functionCalleeAccessors = new Set<MathNode>();

    ast.traverse((node) => {
      if (isAccessorNode(node)) {
        accessorNodes.push(node);
      }
      // Skip `parent.getStorey` in `parent.getStorey()` — it is a call, not a property read
      if (isFunctionNode(node) && isAccessorNode(node.fn)) {
        functionCalleeAccessors.add(node.fn);
      }
      // Skip `parent.parent` in `getProperty(parent.parent, "x")` — the literal is the read
      if (
        isFunctionNode(node) &&
        getFunctionSymbolName(node.fn) === 'getProperty'
      ) {
        let current = node.args[0];
        while (current && isAccessorNode(current)) {
          functionCalleeAccessors.add(current);
          current = current.object;
        }
      }
    });

    accessorNodes.forEach((node) => {
      if (functionCalleeAccessors.has(node)) return;

      const isNestedAccessor = accessorNodes.some(
        (other) =>
          other !== node && isAccessorNode(other) && other.object === node,
      );
      if (isNestedAccessor) return;

      const unwrapped = unwrapAccessorChain(node);
      if (!unwrapped) return;

      const root = resolvePropertyAccessRoot(unwrapped.base);
      if (!root || unwrapped.path.length === 0) return;

      accesses.push({ root, path: unwrapped.path });
    });

    ast.traverse((node) => {
      const parentValuePath = getGetParentValuePath(node);
      if (parentValuePath) {
        accesses.push({ root: 'getParentValue', path: parentValuePath });
      }
      const getPropertyAccess = getGetPropertyAccess(node);
      if (getPropertyAccess) accesses.push(getPropertyAccess);
    });
  } catch {
    return [];
  }

  return dedupePropertyAccesses(accesses);
};

/**
 * Flatten {@link ExpressionPropertyAccess} paths to dot-separated strings for a given root.
 */
export const getExpressionPropertyPathsForRoot = (
  accesses: ExpressionPropertyAccess[],
  root: ExpressionPropertyAccessRoot,
): string[] =>
  uniq(
    accesses
      .filter((access) => access.root === root)
      .map((access) => access.path.join('.')),
  );

/**
 * Symbols reached through inherited `parent` that are not top-level SymbolNodes.
 * Includes member-call names (`parent.getProduct("x")`), nested parent-chain
 * properties (`parent.parent.gfa_residential`), {@link getParentValue} string
 * arguments, and {@link getProperty} string-literal property names.
 */
export const getInheritedExpressionSymbols = (
  expression: string | undefined,
): string[] => {
  if (!expression) return [];

  const symbols = new Set<string>();

  const accesses = getExpressionPropertyAccesses(expression);
  const inheritedRoots: ExpressionPropertyAccessRoot[] = [
    'parent',
    'getParentValue',
  ];
  inheritedRoots.forEach((root) => {
    getExpressionPropertyPathsForRoot(accesses, root).forEach((path) => {
      path.split('.').forEach((segment) => {
        if (segment && segment !== 'parent') symbols.add(segment);
      });
    });
  });

  try {
    mathJS.parse(expression).traverse((node) => {
      if (!isFunctionNode(node)) return;
      const methodName = getParentMemberCallName(node.fn);
      if (methodName) symbols.add(methodName);
    });
  } catch {
    return Array.from(symbols);
  }

  return Array.from(symbols);
};

/**
 * Whether any used path references `propertyKey` (exact match or nested path).
 */
export const isExpressionPropertyPathInUse = (
  usedPaths: string[],
  propertyKey: string,
): boolean =>
  usedPaths.some(
    (path) => path === propertyKey || path.startsWith(`${propertyKey}.`),
  );

/**
 * Whether a dot-path on {@link getStorey} matches {@link StoreySummaryVariables} (single segment only).
 */
export const isAllowedGetStoreyPropertyPath = (path: string): boolean => {
  const [rootSegment, ...nested] = path.split('.').filter(Boolean);
  if (!rootSegment || nested.length > 0) return false;
  return ALLOWED_GET_STOREY_PROPERTY_KEYS.has(rootSegment);
};

/**
 * Paths used on `getStorey()` that are not exposed by {@link toGetStoreySummary}.
 */
export const getDisallowedGetStoreyPropertyPaths = (
  usedPaths: string[],
): string[] =>
  usedPaths.filter((path) => !isAllowedGetStoreyPropertyPath(path));
